import isEmpty from 'lodash/isEmpty';

import {
  AttributionMode,
  AudienceList,
  Conversion,
  ConversionEvent,
  CustomConversion,
  SchedulerMsgParams,
} from 'modules/domains/campaign';
import { parseISOZonedDate } from 'utils/date';
import { pipe } from 'fp-ts/function';
import { option } from 'fp-ts';
import { omitDeep } from 'deepdash/standalone';
import pickBy from 'lodash/pickBy';
import {
  GenerateUpdateCampaignPayloadOptions,
  UpdateCampaignPayload,
  ListIds,
  UpdatedCampaignFields,
  ImmutableSaveCampaignFields,
} from './types';
import { CAMPAIGN_TYPE_SAVE_NAMES_MAPPER } from './constants';
import { GraphQLCampaign } from '../../types';

/**
 *  @description Conversions
 */
export const updateAttributionModeConfig = (
  newConversions: string[],
  newModeConfig: {
    periodHours: string | null;
    mode: string;
  },
): CustomConversion[] => {
  const periodHours = newModeConfig?.periodHours ? parseInt(newModeConfig?.periodHours, 10) : null;
  return newConversions?.map((conversion: string) => ({
    eventName: conversion,
    attributionMode:
      newModeConfig?.mode === AttributionMode.LastMessaged
        ? AttributionMode.AttributionPeriod
        : newModeConfig.mode,
    attributionPeriodHours:
      newModeConfig?.mode === AttributionMode.LastMessaged && !!newModeConfig?.periodHours
        ? periodHours
        : null,
  }));
};

export const createCustomConversions = (
  events: string[],
  periodHours: string | null,
  mode: AttributionMode,
) => ({
  customConversions: updateAttributionModeConfig(events, {
    periodHours,
    mode,
  }),
});

export const getCampaignConversion = (
  campaign: GraphQLCampaign,
  newCampaignFields: Partial<UpdatedCampaignFields>,
): { customConversions: CustomConversion[] } => {
  if (newCampaignFields?.customConversions)
    return newCampaignFields as { customConversions: CustomConversion[] };

  const events = campaign?.conversions
    ?.map((x: Conversion) => x?.events?.map((y: ConversionEvent) => y?.name))
    .flat();
  const mode = campaign.conversions?.[0]?.events?.[0]?.attributionMode?.name as AttributionMode;
  const periodHours = campaign?.conversions?.[0]?.events?.[0]?.attributionMode?.periodHours;
  return createCustomConversions(events, periodHours?.toLocaleString(), mode);
};

/**
 * Returns recurring campaigns schedulerMsgParams
 */
export const parseRecurringSchedulerMsgParams = (schedulerMsgParams: SchedulerMsgParams) => {
  if (!schedulerMsgParams) return {};

  /**
   * This adds the endDate to the schedulerMsgParams if it existed on the campaign data.
   * @see https://iterable.atlassian.net/browse/CAMP-1162
   */
  const endDateTimestamp = schedulerMsgParams?.endDate?.timestamp;
  const endDate = endDateTimestamp ? parseISOZonedDate(endDateTimestamp)?.valueOf() : null; // convert to milliseconds

  const precreateHours = schedulerMsgParams?.precreateHours;
  const skipDaysOfWeek = schedulerMsgParams?.skipDaysOfWeek;
  const recurDays = schedulerMsgParams?.recurDays;
  const recurSchedule = schedulerMsgParams?.recurSchedule;

  const recurringParams = { recurDays, recurSchedule, endDate, skipDaysOfWeek, precreateHours };

  return pickBy(recurringParams, (n) => n !== undefined);
};

/**
 * Generates schedulerMsgParams
 */
export const getCampaignSchedulerParams = (
  campaign: GraphQLCampaign,
  newCampaignFields: Partial<UpdatedCampaignFields>,
) => {
  const { schedulerMsgParams: existingSchedulerMsgParams } = campaign;
  const recurringParams = parseRecurringSchedulerMsgParams(existingSchedulerMsgParams);

  // `newCampaignFields`'s schedulerMsgParams are populated when any delivery field is changed
  const stoParams = newCampaignFields?.schedulerMsgParams ?? existingSchedulerMsgParams;
  const _schedulerMsgParams = {
    ...stoParams,
    ...recurringParams,
  };

  return (!isEmpty(_schedulerMsgParams) && { schedulerMsgParams: _schedulerMsgParams }) || {};
};

const getLabels = (
  labels: GraphQLCampaign['labels'],
  { projectId }: GenerateUpdateCampaignPayloadOptions,
) => labels?.map((label) => ({ id: label.id, name: label.name, projectId }));

export const getCampaignLabels = (
  campaign: GraphQLCampaign,
  options: GenerateUpdateCampaignPayloadOptions,
) => ({
  labels: getLabels(campaign?.labels, options) || [],
});

export const getCampaignType = (campaign: GraphQLCampaign) =>
  CAMPAIGN_TYPE_SAVE_NAMES_MAPPER[campaign.campaignTypeDefinition];

export const getListIds = (lists: AudienceList[], newLists?: ListIds) =>
  newLists || lists?.map((x) => x.id) || [];

export const getCampaignSendListIds = (
  campaign: GraphQLCampaign,
  newCampaignFields: Partial<UpdatedCampaignFields>,
) => ({
  listIds: getListIds(campaign?.lists, newCampaignFields?.listIds),
});

export const getCampaignSuppressionList = (
  campaign: GraphQLCampaign,
  newCampaignFields: Partial<UpdatedCampaignFields>,
) => ({
  suppressionListIds: getListIds(campaign?.suppressionLists, newCampaignFields?.suppressionListIds),
});

/**
 * Returns fields that can/should not be updated on the frontend
 */
const getImmutableCampaignPayloadFields = (
  campaign: GraphQLCampaign,
): ImmutableSaveCampaignFields => ({
  campaignIdOpt: campaign?.id,
  messageMedium: campaign.messageMedium,
  campaignType: getCampaignType(campaign),
});

type DefaultFields = Required<
  Pick<
    UpdateCampaignPayload,
    | 'name'
    | 'labels'
    | 'listIds'
    | 'suppressionListIds'
    | 'customQuietHours'
    | 'customRateLimit'
    | 'ignoreFrequencyCap'
    | 'mobileAppIds'
    | 'dataScienceOptimizations'
    | 'priorityLevel'
    | 'aiCreation'
  >
>;

/**
 * Generates default values for fields available in the campaign already
 * excluding fields from `getImmutableCampaignPayloadFields`
 */
const getDefaultCampaignPayloadFieldValues = (
  campaign: GraphQLCampaign,
  options: GenerateUpdateCampaignPayloadOptions,
): DefaultFields => ({
  name: campaign.name,
  labels: getLabels(campaign.labels, options),
  listIds: getListIds(campaign.lists),
  suppressionListIds: getListIds(campaign.suppressionLists),
  customQuietHours: campaign?.customQuietHours,
  customRateLimit: campaign?.customRateLimit,
  ignoreFrequencyCap: campaign?.ignoreFrequencyCap,
  mobileAppIds: campaign?.mobileAppIds,
  dataScienceOptimizations: campaign?.dataScienceOptimizations,
  priorityLevel: campaign?.priorityLevel,
  aiCreation: campaign?.aiCreation,
  // NOTE: default values for `customConversions` and `schedulerParams` are computed/added in `generateUpdateCampaignPayload`
});

/**
 * Generates a payload for updating an existing campaign.
 * Note: this still require understanding of the endpoint as,
 * for example, updating STO requires also updating `schedulerParams` and not just the `windowHours` field
 */
export const generateUpdateCampaignPayload = (
  campaign: GraphQLCampaign,
  updatedFields: Partial<UpdatedCampaignFields>,
  options: GenerateUpdateCampaignPayloadOptions,
): UpdateCampaignPayload => {
  return pipe(
    option.Do,
    option.bind('defaultFields', () =>
      option.some(getDefaultCampaignPayloadFieldValues(campaign, options)),
    ),
    option.bind('labelFields', () => option.some(getCampaignLabels(campaign, options))),
    option.bind('conversionFields', () =>
      option.some(getCampaignConversion(campaign, updatedFields)),
    ),
    option.bind('sendListFields', () =>
      option.some(getCampaignSendListIds(campaign, updatedFields)),
    ),
    option.bind('suppressionListFields', () =>
      option.some(getCampaignSuppressionList(campaign, updatedFields)),
    ),
    option.bind('schedulerParamFields', () =>
      option.some(getCampaignSchedulerParams(campaign, updatedFields)),
    ),
    option.bind('immutableFields', () => option.some(getImmutableCampaignPayloadFields(campaign))),
    option.map(
      ({
        conversionFields,
        defaultFields,
        immutableFields,
        labelFields,
        schedulerParamFields,
        sendListFields,
        suppressionListFields,
      }) => {
        return {
          ...defaultFields,
          ...conversionFields,
          ...labelFields,
          ...sendListFields,
          ...suppressionListFields,
          ...updatedFields,
          ...schedulerParamFields,
          ...immutableFields,
        };
      },
    ),
    option.map((v) => omitDeep(v, [/__typename\d*$/])), // removes `__typename` fields
    option.map((v) => pickBy(v, (n) => n !== undefined) as UpdateCampaignPayload), // removes `undefined` fields
    option.getOrElse(() => ({}) as UpdateCampaignPayload),
  );
};
